import apiClient from '../client';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';
import { CareLineStatus } from '@/types/careline.types';
import type {
  CareLineResponse,
  CareLineListResponse,
  PatientCareLineResponse,
  EnrollPatientRequest,
  UpdatePatientCareLineRequest,
  DischargePatientRequest,
  CareLineStatistics,
} from '@/types/careline.types';

export const careLinesApi = {
  getAllActive: async (): Promise<CareLineListResponse[]> => {
    const response = await apiClient.get<CareLineListResponse[]>('/v1/care-lines');
    return response.data;
  },

  getAll: async (): Promise<CareLineListResponse[]> => {
    const response = await apiClient.get<CareLineListResponse[]>('/v1/care-lines/all');
    return response.data;
  },

  getById: async (id: string): Promise<CareLineResponse> => {
    const response = await apiClient.get<CareLineResponse>(`/v1/care-lines/${id}`);
    return response.data;
  },

  getByCode: async (code: string): Promise<CareLineResponse> => {
    const response = await apiClient.get<CareLineResponse>(`/v1/care-lines/code/${code}`);
    return response.data;
  },

  getCareLinePatients: async (
    careLineId: string,
    status: CareLineStatus = CareLineStatus.ACTIVE,
    params?: PaginationParams
  ): Promise<PaginatedResponse<PatientCareLineResponse>> => {
    const response = await apiClient.get<PaginatedResponse<PatientCareLineResponse>>(
      `/v1/care-lines/${careLineId}/patients`,
      { params: { status, ...params } }
    );
    return response.data;
  },

  enrollPatient: async (
    careLineId: string,
    data: EnrollPatientRequest
  ): Promise<PatientCareLineResponse> => {
    const response = await apiClient.post<PatientCareLineResponse>(
      `/v1/care-lines/${careLineId}/enroll`,
      data
    );
    return response.data;
  },

  getEnrollment: async (enrollmentId: string): Promise<PatientCareLineResponse> => {
    const response = await apiClient.get<PatientCareLineResponse>(
      `/v1/care-lines/enrollments/${enrollmentId}`
    );
    return response.data;
  },

  updateEnrollment: async (
    enrollmentId: string,
    data: UpdatePatientCareLineRequest
  ): Promise<PatientCareLineResponse> => {
    const response = await apiClient.put<PatientCareLineResponse>(
      `/v1/care-lines/enrollments/${enrollmentId}`,
      data
    );
    return response.data;
  },

  dischargePatient: async (
    enrollmentId: string,
    data: DischargePatientRequest
  ): Promise<PatientCareLineResponse> => {
    const response = await apiClient.post<PatientCareLineResponse>(
      `/v1/care-lines/enrollments/${enrollmentId}/discharge`,
      data
    );
    return response.data;
  },

  getPatientEnrollments: async (patientId: string): Promise<PatientCareLineResponse[]> => {
    const response = await apiClient.get<PatientCareLineResponse[]>(
      `/v1/care-lines/patient/${patientId}`
    );
    return response.data;
  },

  getActivePatientEnrollments: async (patientId: string): Promise<PatientCareLineResponse[]> => {
    const response = await apiClient.get<PatientCareLineResponse[]>(
      `/v1/care-lines/patient/${patientId}/active`
    );
    return response.data;
  },

  getStatistics: async (): Promise<CareLineStatistics> => {
    const response = await apiClient.get<CareLineStatistics>('/v1/care-lines/statistics');
    return response.data;
  },
};
